import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import type { Ref } from 'vue';
import api from '@/api';
import { AdpDocument, isArticle } from '@/types';

// 記事リンクのクリックをきっかけに関連記事を取得・表示するロジック。
// タブが非表示の間は取得を保留し、再表示時に取得する。
export const useRelatedArticles = (
  adpDocument: Ref<AdpDocument | undefined>,
  options: { disableRelatedArticles?: boolean } = {},
) => {
  const isArticleClicked = ref(false);
  const showRelatedArticles = ref(false);
  const relatedArticles = ref<AdpDocument[]>([]);
  const relatedQuery = ref('');
  const requestId = ref('');

  const getRelatedArticlesIfVisible = () => {
    if (document.visibilityState === 'visible' && isArticleClicked.value) {
      getRelatedArticles();
    }
  };

  const getRelatedArticles = async () => {
    if (
      !adpDocument.value ||
      options.disableRelatedArticles ||
      !isArticle(adpDocument.value) ||
      adpDocument.value.lang !== 'ja' ||
      !adpDocument.value.related_articles_available
    )
      return;

    isArticleClicked.value = true;
    document.addEventListener('visibilitychange', getRelatedArticlesIfVisible);
    if (document.visibilityState === 'hidden') return;
    try {
      const response = await api.getRelatedArticles(adpDocument.value.id);
      relatedArticles.value = response.articles;
      relatedQuery.value = response.search_query;
      requestId.value = response.request_id;
    } catch {
      relatedArticles.value = [];
      relatedQuery.value = '';
      requestId.value = '';
    }

    document.removeEventListener(
      'visibilitychange',
      getRelatedArticlesIfVisible,
    );

    await nextTick();
    showRelatedArticles.value = true;
  };

  onMounted(() => {
    document.addEventListener('visibilitychange', getRelatedArticlesIfVisible);
  });

  onBeforeUnmount(() =>
    document.removeEventListener(
      'visibilitychange',
      getRelatedArticlesIfVisible,
    ),
  );

  return {
    showRelatedArticles,
    relatedArticles,
    relatedQuery,
    requestId,
    getRelatedArticles,
  };
};
